import { CLIENT_TAT_TARGETS } from "./constants";

// 1. Auto-determine Status based on Substatus (Updated with specialized status overrides)
export const getStatusFromSubstatus = (subStatus) => {
  if (!subStatus) return "";
  const clean = subStatus.trim().toLowerCase();

  // Explicit Strict Overrides for Wrong Report & Do Not Review
  if (clean.includes("wrong report")) return "Wrong Report";
  if (clean.includes("do not review")) return "Do Not Review";

  // Completed Logic
  if (clean.includes("accepted") || clean.includes("completed")) return "Completed";

  // Rejected Logic
  if (clean.includes("rejected") || clean.includes("wrong r") || clean.includes("denied")) return "Rejected";

  // Revision Logic
  if (clean.includes("revision") || clean.includes("correction")) return "Lender Revision";

  // Default Working Logic
  if (clean.includes("need to proceed") || clean === "working" || clean.includes("progress")) return "Working";

  return "Working"; // Fallback
};



// 3. Shift Logic (Live vs Offline)
export const calculateShift = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const date = parseCustomDate(dateTimeStr);
  if (!date || isNaN(date.getTime())) return "";
  const hour = date.getHours();
  // Live is standard 5AM to 5PM (17:00)
  return (hour >= 5 && hour < 17) ? "Live" : "Offline";
};

// 4. Calculate Turnaround Time (TAT)
export const calculateTAT = (inTime, outTime) => {
  if (!inTime || !outTime) return "";
  const start = parseCustomDate(inTime);
  const end = parseCustomDate(outTime);
  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return "";

  const diffInMs = end - start;
  const totalMinutes = Math.floor(diffInMs / 60000);

  if (totalMinutes < 0) return "Check AM/PM";
  if (totalMinutes === 0) return "0 mins";

  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  return hrs > 0 ? `${hrs} hr ${mins} mins` : `${mins} mins`;
};

// 5. Compare PP vs Market Value using a strict 10% boundary variance rule
export const calculateValueTenPercent = (pp, marketValue) => {
  if (!pp || !marketValue) return "N/A";

  // Clean string inputs by removing commas, dollar signs, or spaces
  const ppNum = Number(String(pp).replace(/[^0-9.-]+/g, ""));
  const mvNum = Number(String(marketValue).replace(/[^0-9.-]+/g, ""));

  // If calculation data is corrupt or missing, return safely
  if (isNaN(ppNum) || isNaN(mvNum) || ppNum === 0) return "N/A";

  // 1. Find 10% of the Purchase Price
  const tenPercentOfPP = ppNum * 0.10;

  // 2. Define acceptable variance limits (Low boundary and High boundary)
  const lowerLimit = ppNum - tenPercentOfPP;
  const upperLimit = ppNum + tenPercentOfPP;

  // 3. If Market Value is outside the 10% window, it "Exceeds" the variance rule
  if (mvNum < lowerLimit || mvNum > upperLimit) {
    return "Exceed";
  }

  // Otherwise, it is safely within the 10% margin
  return "Within";
};

// 6. Date & Time Formatters
export const getDayName = (dateStr) => {
  if (!dateStr) return "";

  let date;
  const cleanStr = String(dateStr).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) {
    const [year, month, day] = cleanStr.split("-").map(Number);
    date = new Date(year, month - 1, day);
  } else if (/^\d{2}-\d{2}-\d{4}$/.test(cleanStr)) {
    const [day, month, year] = cleanStr.split("-").map(Number);
    date = new Date(year, month - 1, day);
  } else if (/^\d{4}\/\d{2}\/\d{2}$/.test(cleanStr)) {
    const [year, month, day] = cleanStr.split("/").map(Number);
    date = new Date(year, month - 1, day);
  } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(cleanStr)) {
    const [month, day, year] = cleanStr.split("/").map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(cleanStr);
  }

  return isNaN(date.getTime()) ? "" : date.toLocaleDateString('en-US', { weekday: 'short' });
};

export const formatDateOnly = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const date = parseCustomDate(dateTimeStr);
  return (!date || isNaN(date.getTime())) ? "" : date.toLocaleDateString('en-GB'); // DD/MM/YYYY
};

export const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const date = parseCustomDate(dateTimeStr);
  if (!date || isNaN(date.getTime())) return "";

  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');

  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

export const parseCustomDate = (str) => {
  if (!str) return null;
  let clean = String(str).trim();

  // 1. Remove duplicate/contradictory AM/PM if hour is > 12 (e.g. "14:12 PM" -> "14:12")
  const timeWithAmPmMatch = clean.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]M)/i);
  let ampm = null;
  if (timeWithAmPmMatch) {
    ampm = timeWithAmPmMatch[4].toUpperCase();
    const hr = parseInt(timeWithAmPmMatch[1], 10);
    if (hr > 12) {
      clean = clean.replace(/\s*[AP]M/i, "");
      ampm = null;
    }
  }

  // 2. If time is at the beginning (e.g. "14:12 7/2/2026" or "2:12 PM 7/2/2026")
  const timeFirstRegex = /^(\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AP]M)?)\s+(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{2,4})$/i;
  const match = clean.match(timeFirstRegex);
  if (match) {
    const timePart = match[1];
    const datePart = match[2];
    clean = `${datePart} ${timePart}`;
  }

  // Match "DD/MM/YYYY HH:MM:SS" or "MM/DD/YYYY HH:MM:SS" or dash versions
  const dmyRegex = /^(\d{1,2})[\-\/](\d{1,2})[\-\/](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:\s*([AP]M))?$/i;
  const dmyMatch = clean.match(dmyRegex);
  if (dmyMatch) {
    const part1 = parseInt(dmyMatch[1], 10);
    const part2 = parseInt(dmyMatch[2], 10);
    const year = parseInt(dmyMatch[3], 10);
    let hour = dmyMatch[4] ? parseInt(dmyMatch[4], 10) : 0;
    const min = dmyMatch[5] ? parseInt(dmyMatch[5], 10) : 0;
    const sec = dmyMatch[6] ? parseInt(dmyMatch[6], 10) : 0;
    const cellAmPm = dmyMatch[7] ? dmyMatch[7].toUpperCase() : ampm;

    if (cellAmPm) {
      if (cellAmPm === "PM" && hour < 12) hour += 12;
      if (cellAmPm === "AM" && hour === 12) hour = 0;
    }

    let day, month;
    if (part2 > 12) {
      // part2 is > 12 so part2 MUST be Day, part1 MUST be Month (e.g. 08/30/2026)
      month = part1;
      day = part2;
    } else if (part1 > 12) {
      // part1 is > 12 so part1 MUST be Day, part2 MUST be Month (e.g. 30/08/2026)
      day = part1;
      month = part2;
    } else if (clean.includes("/")) {
      // Slash format with both <= 12: default to US MM/DD/YYYY
      month = part1;
      day = part2;
    } else {
      // Dash format with both <= 12: default to standard DD-MM-YYYY
      day = part1;
      month = part2;
    }

    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const d = new Date(year, month - 1, day, hour, min, sec);
      if (!isNaN(d.getTime())) return d;
    }
  }

  // Match "YYYY-MM-DD HH:MM:SS" or "YYYY/MM/DD HH:MM:SS"
  const ymdRegex = /^(\d{4})[\-\/](\d{1,2})[\-\/](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:\s*([AP]M))?$/i;
  const ymdMatch = clean.match(ymdRegex);
  if (ymdMatch) {
    const year = parseInt(ymdMatch[1], 10);
    const month = parseInt(ymdMatch[2], 10);
    const day = parseInt(ymdMatch[3], 10);
    let hour = ymdMatch[4] ? parseInt(ymdMatch[4], 10) : 0;
    const min = ymdMatch[5] ? parseInt(ymdMatch[5], 10) : 0;
    const sec = ymdMatch[6] ? parseInt(ymdMatch[6], 10) : 0;
    const cellAmPm = ymdMatch[7] ? ymdMatch[7].toUpperCase() : ampm;

    if (cellAmPm) {
      if (cellAmPm === "PM" && hour < 12) hour += 12;
      if (cellAmPm === "AM" && hour === 12) hour = 0;
    }

    const d = new Date(year, month - 1, day, hour, min, sec);
    if (!isNaN(d.getTime())) return d;
  }

  const d = new Date(clean);
  if (isNaN(d.getTime())) return null;
  return d;
};

// 7. Robust Cross-Stream SLA Performance Engine (Strict uniform case-matching)
export const calculateSLA = (tatStr, clientName, department, priority, clientTatMap = {}) => {
  if (!tatStr) return "";
  if (tatStr === "0 mins") return "Within SLA";

  // Convert localized string to scalar total minutes
  let totalMins = 0;
  const hrMatch = tatStr.match(/(\d+)\s*hr/i);
  const minMatch = tatStr.match(/(\d+)\s*mins?/i);

  if (hrMatch) totalMins += parseInt(hrMatch[1], 10) * 60;
  if (minMatch) totalMins += parseInt(minMatch[1], 10);

  if (totalMins === 0 && !tatStr.includes("Check AM/PM")) return "N/A";

  // ✅ CASE-INSENSITIVE STANDARDIZATION: Guarantees imported sheets match typed form entries seamlessly
  const normalizedClient = String(clientName || "").trim().toLowerCase();
  const normalizedDept = String(department || "").trim().toLowerCase();
  const normalizedPriority = String(priority || "").trim().toLowerCase();

  const isRevision = normalizedDept === "report writing" || normalizedDept === "revised";
  const isPriority = normalizedPriority.includes("rush") ||
    normalizedPriority.includes("due") ||
    normalizedPriority.includes("vip") ||
    normalizedPriority.includes("late");

  // Set system operational defaults fallback (2 hours for QC, 1 hour for Revisions)
  let targetHours = isRevision ? 1.0 : 2.0;

  // 1. Check if client targets are dynamically defined in clientTatMap
  if (clientTatMap && clientTatMap[normalizedClient]) {
    const config = clientTatMap[normalizedClient];
    if (isRevision) {
      targetHours = isPriority ? config.priority_revised : config.normal_revised;
    } else {
      targetHours = isPriority ? config.priority_qc : config.normal_qc;
    }
  }
  // 2. Fallback to hardcoded rules
  else if (normalizedClient === "ascribe vl") {
    if (isRevision) {
      targetHours = CLIENT_TAT_TARGETS["ascribe vl"].revised; // 1 hour
    }
    // ✅ ONLY change to 2 hours if an explicit priority keyword is selected
    else if (isPriority) {
      targetHours = CLIENT_TAT_TARGETS["ascribe vl"].priority_qc; // 2 hours
    }
    // ✅ DEFAULT fallback: If it's blank ("") or anything else, it gets 4 hours!
    else {
      targetHours = CLIENT_TAT_TARGETS["ascribe vl"].normal_qc; // 4 hours
    }
  }
  else if (CLIENT_TAT_TARGETS[normalizedClient]) {
    const config = CLIENT_TAT_TARGETS[normalizedClient];
    if (isRevision) {
      targetHours = config.revised !== undefined ? config.revised : 1.0;
    } else {
      targetHours = config.qc !== undefined ? config.qc : 2.0;
    }
  }

  // Final evaluation benchmark mapping check
  const targetMinutes = targetHours * 60;
  return totalMins > targetMinutes ? "Over SLA" : "Within SLA";
};

// 8. Dynamic SLA Target Hours Resolver
export const getClientSlaTargetHours = (clientName, department, priority, clientTatMap = {}) => {
  const normalizedClient = String(clientName || "").trim().toLowerCase();
  const normalizedDept = String(department || "").trim().toLowerCase();
  const normalizedPriority = String(priority || "").trim().toLowerCase();

  const isRevision = normalizedDept === "report writing" || normalizedDept === "revised" || normalizedDept.includes("revision");
  const isPriority = normalizedPriority.includes("rush") ||
    normalizedPriority.includes("due") ||
    normalizedPriority.includes("vip") ||
    normalizedPriority.includes("late");

  // Set system operational defaults fallback (2 hours for QC, 1 hour for Revisions)
  let targetHours = isRevision ? 1.0 : 2.0;

  // 1. Check if client targets are dynamically defined in clientTatMap
  if (clientTatMap && clientTatMap[normalizedClient]) {
    const config = clientTatMap[normalizedClient];
    if (isRevision) {
      targetHours = isPriority ? config.priority_revised : config.normal_revised;
    } else {
      targetHours = isPriority ? config.priority_qc : config.normal_qc;
    }
  }
  // 2. Fallback to hardcoded rules
  else if (normalizedClient === "ascribe vl") {
    if (isRevision) {
      targetHours = (CLIENT_TAT_TARGETS["ascribe vl"] && CLIENT_TAT_TARGETS["ascribe vl"].revised) !== undefined 
        ? CLIENT_TAT_TARGETS["ascribe vl"].revised : 1.0;
    }
    else if (isPriority) {
      targetHours = (CLIENT_TAT_TARGETS["ascribe vl"] && CLIENT_TAT_TARGETS["ascribe vl"].priority_qc) !== undefined 
        ? CLIENT_TAT_TARGETS["ascribe vl"].priority_qc : 2.0;
    }
    else {
      targetHours = (CLIENT_TAT_TARGETS["ascribe vl"] && CLIENT_TAT_TARGETS["ascribe vl"].normal_qc) !== undefined 
        ? CLIENT_TAT_TARGETS["ascribe vl"].normal_qc : 4.0;
    }
  }
  else if (CLIENT_TAT_TARGETS[normalizedClient]) {
    const config = CLIENT_TAT_TARGETS[normalizedClient];
    if (isRevision) {
      targetHours = config.revised !== undefined ? config.revised : 1.0;
    } else {
      targetHours = config.qc !== undefined ? config.qc : 2.0;
    }
  }

  return targetHours;
};

// 9. Real-Time Active SLA Row Color Resolver
export const getActiveRowColor = (f, clientTatMap = {}) => {
  if (!f.in_time || f.in_time === "-") return null;

  const statusVal = String(f.status || "").trim().toLowerCase();
  const substatus = String(f.substatus || "").trim().toLowerCase();

  const hasOutTime = f.out_time && f.out_time !== "" && f.out_time !== "-";
  const isClosedOrException = 
      hasOutTime ||
      statusVal === "completed" || statusVal === "rejected" || 
      statusVal === "wrong report" || statusVal === "do not review" || statusVal === "complete by client" ||
      substatus === "wrong report" || substatus === "do not review" || substatus === "complete by client";

  if (isClosedOrException) return null;

  const start = parseCustomDate(f.in_time);
  if (!start || isNaN(start.getTime())) return null;

  const elapsedMs = new Date() - start;
  const elapsedMins = Math.floor(elapsedMs / 60000);

  const targetHours = getClientSlaTargetHours(
      f.client_name || f.activeClient || "",
      f.department || f.activeDept || "",
      f.priority || "",
      clientTatMap
  );
  const targetMinutes = Math.round(targetHours * 60);
  const remainingMinutes = targetMinutes - elapsedMins;

  const isRevision = String(f.department || f.activeDept || "").trim().toLowerCase() === "report writing" || 
                     String(f.department || f.activeDept || "").trim().toLowerCase() === "revised" ||
                     statusVal === "lender revision";

  if (isRevision) {
    // Revision rules (Needs 15 mins to complete)
    if (remainingMinutes <= 15) return "#fee2e2"; // Red
  } else {
    // QC rules (Needs 30 mins to complete)
    if (targetMinutes <= 30) {
      if (remainingMinutes <= 15) return "#fee2e2"; // Red
    } else {
      if (remainingMinutes <= 30) return "#fee2e2"; // Red
    }
  }

  return null;
};

// 10. Real-Time Active SLA & TAT Countdown Resolver
export const calculateActiveSLA = (inTimeStr, clientName, department, priority, clientTatMap = {}) => {
  if (!inTimeStr || inTimeStr === "-") return { tat: "-", status: "-" };

  const start = parseCustomDate(inTimeStr);
  if (!start || isNaN(start.getTime())) return { tat: "-", status: "-" };

  const elapsedMs = new Date() - start;
  const elapsedMins = Math.floor(elapsedMs / 60000);

  if (elapsedMins < 0) {
    if (elapsedMins >= -15) {
      return { tat: "0 mins", status: "Within SLA" };
    }
    return { tat: "Check InTime", status: "-" };
  }

  // Calculate dynamic TAT string
  const hrs = Math.floor(elapsedMins / 60);
  const mins = elapsedMins % 60;
  const tatStr = hrs > 0 ? `${hrs} hr ${mins} mins` : `${mins} mins`;

  // Get SLA target limit
  const targetHours = getClientSlaTargetHours(clientName, department, priority, clientTatMap);
  const targetMinutes = Math.round(targetHours * 60);

  if (elapsedMins > targetMinutes) {
    return {
      tat: tatStr,
      status: "Over SLA"
    };
  } else {
    return {
      tat: tatStr,
      status: "Within SLA"
    };
  }
};
